// Ionic & Angular Imports
import {Component} from '@angular/core';
import {Events, NavParams, LoadingController, Loading, ModalController, AlertController, Refresher} from 'ionic-angular';

// Service Imports 
import {rsReportSvc, ReportState} from '../../../../services/report.service';
import {rsLaborSvc, Labor} from '../../../../services/labor.service';
import {IEmployee} from '../../../../services/employee.service';

// Component Imports
import {BasePage} from '../../../base.component';
import {LaborModal} from './labor-modal/labor-modal.component';

@Component({
    templateUrl: 'labor.html',
    providers: [
        rsLaborSvc
    ]
})
export class LaborPage extends BasePage
{
    // Class variables 
    private contentList : Labor[];
    private employeeList : IEmployee[];

    // Class Constructor
    constructor( 
        protected alertCtrl : AlertController, 
        private loadingCtrl : LoadingController,  
        private navParams : NavParams, 
        private eventSystem : Events,
        private modalCtrl : ModalController,
        private reportSvc: rsReportSvc, 
        private laborSvc : rsLaborSvc
    ){
        // Construct base class
        super(alertCtrl);

        interface NavData {
            employees : IEmployee[];
            laborers : Labor[];
        };

        // Retrieve the activity list from our NavParams       
        let navData : NavData = <NavData>this.navParams.get('content');

        this.contentList = navData.laborers;
        this.employeeList = navData.employees;
        this.sortContentList(); this.sortEmployeeList();

        // Register our event handlers 
        this.registerEventHandlers( eventSystem );
    }

    // Class method used to subscribe to all events this class is interested in
    private registerEventHandlers( eventSystem : Events ) {
        eventSystem.subscribe( 'labor:deleted', (data) => { this.deleteLabor( data ); });
        eventSystem.subscribe( 'labor:updated', (data) => { this.updateLabor( data ); });
        eventSystem.subscribe( 'labor:added',   (data) => { this.addLabor( data ); });
    }

   // Sorting method for ensuring our ContentList is always sorted by id (ie., oldest to newest entries)
    private sortContentList(reverse : boolean = false) {
        this.contentList.sort( (a,b) => {
            return reverse ? b.id - a.id : a.id - b.id;
        });
    }
    private sortEmployeeList(reverse : boolean = false ) {
        this.employeeList.sort( (a, b) => {
            let nameA : string = (a.lastname + ", " + a.firstname).toLowerCase();
            let nameB : string = (b.lastname + ", " + b.firstname).toLowerCase();
            if( nameA < nameB ) return -1;
            if( nameA > nameB ) return 1;
            return 0;
        });
    }

    public getLaborAvatar( labor : Labor ) : string {
        return "http://placehold.it/64x64";
    }

    // Class method used to add a new labor to our ContentList 
    private addLabor( labor : Labor ) : boolean {
        // Add this labor to the end of our ContentList  
        let length : number = this.contentList.push(labor);

        // Finally, publish an event that our report has been updated
        this.eventSystem.publish( 'report:updated', ReportState.Labor, length );      
        
        // Return successful
        return true;  
    }

    // Class method used to update an labor in our ContentList
    private updateLabor( labor : Labor ) : boolean {
        // Loop through each item in the ContentList to attempt to find a matching labor
        for( var idx = 0; idx < this.contentList.length; idx++ ) {
            let item : Labor = this.contentList[idx];
            if( item.id === labor.id ) {
                // Update the content of this labor within the ContentList
                this.contentList[idx] = labor;
                return true;          
            }
        }
        // No match was found, return false 
        return false;
    }

    // Class method used to delete an labor in our ContentList
    private deleteLabor( labor : Labor ) : boolean {
        // Loop through each item in the ContentList to attempt to find a matching labor
        for( var idx = 0; idx < this.contentList.length; idx++ ) {
            let item : Labor = this.contentList[idx];
            if( item.id === labor.id ) {
                // Remove this labor from the ContentList
                this.contentList.splice( idx, 1 );

                // Publish an event that will update our report state 
                this.eventSystem.publish( 'report:updated', ReportState.Labor, this.contentList.length );

                // Return successful
                return true;          
            }
        }
        // No match was found, return false 
        return false;
    }

    // Class method used by the template to edit an existing activity
    public updateContent( labor : Labor ) {
        // Create our ModalInstance and configure for editing
        this.modalCtrl.create( LaborModal, { 
            labor: labor,
            employees: this.employeeList,
            editmode: true
        }).present();
    }

    // Class method used by the template to create a new activity
    public createContent( ) {
          // Create our ModalInstance and configure for creating 
        this.modalCtrl.create( LaborModal, { 
            labor: null,
            employees: this.employeeList,
            editmode: false
        }).present();      
    }

    // Class method used by the template to delete an activity
    public deleteContent( labor : Labor ) {
        // Verify that we have a valid activity id before trying to delete 
        if( labor.id > 0 ) 
        {
            // Labor id is valid; instantiate delete confirmation
            this.displayConfirmation( 'Delete Labor', 'Are you sure you want to delete "' + labor.employee.firstname + ' ' + labor.employee.lastname + '" from this report?', () => 
            {
                // User confirmed- so let's initiate the delete process
                let loader : Loading = this.loadingCtrl.create( {
                    content: 'Deleting...'
                });
                loader.present().then( () => {
                    // Data hNow let's start our delete process 
                    this.laborSvc.deleteLabor( labor.id, this.reportSvc.getActiveReportId() ).subscribe( result => {
                        // Data has been received from our service 
                        this.eventSystem.publish('labor:deleted', labor );
                    }, err => {
                        // Operation failed; dismiss loader and shown an error message 
                        loader.dismiss().then( () => {
                            this.displayError("There was an error deleting this laborer.  Please try again later.");
                        });
                    }, () => {
                        // Operation succeeded, dismiss loader
                        loader.dismiss();
                    });
                });
            });
        } else {
            // The labor id supplied is not valid; display a generic error 
            this.displayError( 'An error has occurred.  Please try again later.' );
        }
    }

    // Class method used by the template to refresh the ContentList
    public refreshContent( refresher : Refresher ) {
        // Spawn a spinner while the ContentList is being refreshed
        let loader : Loading = this.loadingCtrl.create( {
            content: 'Refreshing...'
        });
        loader.present().then( () => {
            // The spinner is loaded, so let's get our refreshed ContentList
            this.laborSvc.getLaborers( this.reportSvc.getActiveReportId() )
            .subscribe( result => {
                // Data was received from our service 
                this.contentList = result;
                this.sortContentList();

                // Publish an event that will update our report state 
                this.eventSystem.publish( 'report:updated', ReportState.Labor, this.contentList.length );
            }, err => {
                // The operation failed, so let's handle this error 
                loader.dismiss().then( () => {
                    this.displayError( "There was an error refreshing the list.  Please try again later." ).then( () => {
                        refresher.complete();
                    });
                });
            }, () => {
                // The operation succeeded, let's dismiss our spinner and complete our refresh operation 
                loader.dismiss().then( () => {
                    refresher.complete();
                });
            });
        });
    }    
}


// WEBPACK FOOTER //
// ./src/pages/report/tab-main/labor/labor.component.ts